import type { CompletedActivityRecord } from "../completedActivityHistory";
import type { PreferredUnits } from "../../athlete-profile/types/athleteProfile";
import { formatDistanceFromMeters } from "../../../lib/unitPreferences";

type ActivityHistoryPanelProps = {
  records: CompletedActivityRecord[];
  onOpenAnalysis: (activityId: string) => void;
  onDeleteActivity: (activityId: string) => void;
  preferredUnits?: PreferredUnits;
};

function formatDuration(seconds: number | null): string {
  if (seconds === null || !Number.isFinite(seconds)) return "--";
  const wholeSeconds = Math.max(0, Math.round(seconds));
  const hours = Math.floor(wholeSeconds / 3600);
  const minutes = Math.floor((wholeSeconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function formatDistance(distanceMeters: number | null, preferredUnits: PreferredUnits): string {
  if (distanceMeters === null || !Number.isFinite(distanceMeters)) return "--";
  return formatDistanceFromMeters(distanceMeters, preferredUnits);
}

function formatConfidence(value: number): "High" | "Medium" | "Low" {
  if (value >= 80) return "High";
  if (value >= 60) return "Medium";
  return "Low";
}

export function ActivityHistoryPanel({ records, onOpenAnalysis, onDeleteActivity, preferredUnits = "imperial" }: ActivityHistoryPanelProps) {
  return (
    <section className="activity-history-panel" aria-labelledby="activity-history-title">
      <div className="activity-history-header">
        <div>
          <p className="eyebrow">ACTIVITY HISTORY</p>
          <h2 id="activity-history-title">Completed analyses</h2>
        </div>
        <span className="coach-summary-badge">{records.length} activities</span>
      </div>

      {records.length === 0 ? (
        <p className="command-note">No completed activities are saved yet.</p>
      ) : (
        <ul className="activity-history-list">
          {records.map(record => (
            <li key={record.activityId} className="activity-history-item">
              <div>
                <strong>{record.activityTitle}</strong>
                <p className="command-note">
                  {new Date(record.activityDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                  {" · "}
                  {record.rideClassification.displayName}
                  {" · "}
                  {formatDuration(record.durationSeconds)}
                  {" · "}
                  {formatDistance(record.distanceMeters, preferredUnits)}
                </p>
                <p className="command-note">
                  FTP {record.ftpSnapshot.ftpWatts ?? "--"} W · Confidence {formatConfidence(record.analysisConfidence.overallPercent)} · {record.analysisVersion}
                </p>
              </div>
              <div className="activity-history-actions">
                <button type="button" className="ride-analysis-button secondary-action" onClick={() => onOpenAnalysis(record.activityId)}>Open Analysis</button>
                <button
                  type="button"
                  className="ride-analysis-button secondary-action"
                  onClick={() => {
                    if (window.confirm("Delete this activity from history?")) {
                      onDeleteActivity(record.activityId);
                    }
                  }}
                >
                  Delete from History
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
